// src/components/home/CommentList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import {
  collection, query, orderBy, onSnapshot, addDoc, deleteDoc,
  doc, serverTimestamp, increment, updateDoc,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { formatRelativeTime } from '../../utils/feedHelpers';

const CommentList = ({ postId, open }) => {
  const { user, userData, isAdmin } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  /* Live comments */
  useEffect(() => {
    if (!open) return;
    const q = query(
      collection(db, 'posts', postId, 'comments'),
      orderBy('createdAt', 'asc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.warn('comments listener:', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [postId, open]);

  const submit = useCallback(async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'posts', postId, 'comments'), {
        authorId: user.uid,
        authorName: userData?.displayName || user.displayName || 'User',
        authorPhoto: userData?.photoUrl || null,
        text: text.trim(),
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'posts', postId), {
        commentCount: increment(1),
      });
      setText('');
    } catch (err) {
      console.error('comment failed:', err);
    } finally {
      setSending(false);
    }
  }, [text, sending, postId, user, userData]);

  const del = useCallback(
    async (commentId) => {
      if (!window.confirm('Delete this comment?')) return;
      try {
        await deleteDoc(doc(db, 'posts', postId, 'comments', commentId));
        await updateDoc(doc(db, 'posts', postId), {
          commentCount: increment(-1),
        });
      } catch (err) {
        console.error('delete comment failed:', err);
      }
    },
    [postId],
  );

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  if (!open) return null;

  return (
    <div className="border-t dark:border-gray-700 pt-3 mt-3">
      {loading ? (
        <p className="text-center text-xs text-gray-500 py-2">Loading…</p>
      ) : comments.length === 0 ? (
        <p className="text-center text-xs text-gray-500 py-2">
          No comments yet. Be the first!
        </p>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {comments.map((c) => {
            const canDelete = c.authorId === user.uid || isAdmin;
            return (
              <div key={c.id} className="flex gap-2 group">
                <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0 overflow-hidden">
                  {c.authorPhoto ? (
                    <img src={c.authorPhoto} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-200">
                      {c.authorName?.[0]?.toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl px-3 py-2">
                    <p className="text-xs font-semibold dark:text-white">
                      {c.authorName}
                    </p>
                    <p className="text-sm dark:text-gray-100 whitespace-pre-wrap break-words">
                      {c.text}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 pl-2">
                    <span className="text-[10px] text-gray-500">
                      {formatRelativeTime(c.createdAt)}
                    </span>
                    {canDelete && (
                      <button
                        onClick={() => del(c.id)}
                        className="text-[10px] text-red-500 hover:underline opacity-0 group-hover:opacity-100 transition"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-2 mt-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center font-bold text-xs flex-shrink-0 overflow-hidden">
          {userData?.photoUrl ? (
            <img src={userData.photoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            (userData?.displayName || user?.email || 'U')[0]?.toUpperCase()
          )}
        </div>
        <div className="flex-1 relative">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            disabled={sending}
            placeholder="Write a comment…"
            className="w-full bg-gray-100 dark:bg-gray-700 rounded-full px-4 py-2 pr-10 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
          />
          <button
            onClick={submit}
            disabled={!text.trim() || sending}
            aria-label="Send comment"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-orange disabled:text-gray-400"
          >
            <i className="fas fa-paper-plane text-sm" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommentList;